import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { formatXAF, formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle } from "lucide-react";

export default function Fournisseurs() {
  const { t } = useI18n();
  const [suppliers, setSuppliers] = useState([]);
  const [stock, setStock] = useState([]);
  const [summary, setSummary] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [openS, setOpenS] = useState(false);
  const [tab, setTab] = useState("stock");
  const [openI, setOpenI] = useState(false);
  const [openPu, setOpenPu] = useState(false);
  const [newS, setNewS] = useState({ name: "", category: "", phone: "", city: "", email: "" });
  const [newI, setNewI] = useState({ name: "", category: "", unit: "", quantity: "", min_threshold: "", unit_cost: "", supplier_id: "" });
  const [newPu, setNewPu] = useState({ supplier_id: "", category: "", item: "", quantity: "", unit_cost: "", date: "" });

  async function loadAll() {
    const [s, i, sum, pu] = await Promise.all([
      api.get("/suppliers"), api.get("/stock_items"),
      api.get("/reports/suppliers"), api.get("/purchases"),
    ]);
    setSuppliers(s.data); setStock(i.data); setSummary(sum.data); setPurchases(pu.data);
  }
  useEffect(() => { loadAll(); }, []);

  // Pré-remplissage via l'assistant IA (action create_supplier)
  useEffect(() => {
    const raw = sessionStorage.getItem("ai_prefill");
    if (!raw) return;
    try {
      const a = JSON.parse(raw);
      if (a.type === "create_supplier") {
        setNewS((prev) => ({ ...prev, name: a.name || "", category: a.category || "", phone: a.phone || "", city: a.city || "", email: a.email || "" }));
        setTab("suppliers");
        setOpenS(true);
        toast.success(t("ai_prefilled"));
      }
    } catch { /* ignore */ }
    sessionStorage.removeItem("ai_prefill");
  }, []);

  async function addSupplier() {
    if (!newS.name) return toast.error("Nom requis");
    await api.post("/suppliers", newS);
    setOpenS(false); setNewS({ name: "", category: "", phone: "", city: "", email: "" });
    toast.success(t("added")); loadAll();
  }
  async function addItem() {
    if (!newI.name) return toast.error("Nom requis");
    const sup = suppliers.find((s) => s.id === newI.supplier_id);
    await api.post("/stock_items", {
      ...newI, quantity: parseFloat(newI.quantity || 0),
      min_threshold: parseFloat(newI.min_threshold || 0),
      unit_cost: parseFloat(newI.unit_cost || 0),
      supplier_name: sup?.name,
    });
    setOpenI(false); setNewI({ name: "", category: "", unit: "", quantity: "", min_threshold: "", unit_cost: "", supplier_id: "" });
    toast.success(t("added")); loadAll();
  }
  async function delSupplier(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/suppliers/${id}`); toast.success(t("deleted")); loadAll();
  }
  async function delItem(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/stock_items/${id}`); toast.success(t("deleted")); loadAll();
  }
  async function addPurchase() {
    if (!newPu.supplier_id || !newPu.item) return toast.error("Fournisseur & article requis");
    const sup = suppliers.find((s) => s.id === newPu.supplier_id);
    const qty = parseFloat(newPu.quantity || 0);
    const cost = parseFloat(newPu.unit_cost || 0);
    await api.post("/purchases", {
      ...newPu, supplier_name: sup?.name, quantity: qty, unit_cost: cost, total: qty * cost,
      date: newPu.date ? new Date(newPu.date).toISOString() : new Date().toISOString(),
    });
    setOpenPu(false); setNewPu({ supplier_id: "", category: "", item: "", quantity: "", unit_cost: "", date: "" });
    toast.success(t("added")); loadAll();
  }
  async function delPurchase(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/purchases/${id}`); toast.success(t("deleted")); loadAll();
  }

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div>
        <div className="label-eyebrow mb-2">Module 3</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("suppliers")}</h1>
        <p className="text-muted-foreground mt-1">{suppliers.length} fournisseurs · {stock.length} intrants</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="stock" data-testid="tab-stock">{t("stock_items")}</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">{t("suppliers")}</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="tab-purchases">{t("purchases")}</TabsTrigger>
        </TabsList>

        <TabsContent value="stock" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={openI} onOpenChange={setOpenI}>
              <DialogTrigger asChild><Button data-testid="add-item-btn"><Plus className="w-4 h-4 mr-2" />{t("stock_items")}</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("add")} — {t("stock_items")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>{t("name")}</Label><Input data-testid="item-name" value={newI.name} onChange={(e) => setNewI({ ...newI, name: e.target.value })} /></div>
                  <div><Label>{t("category")}</Label><Input value={newI.category} onChange={(e) => setNewI({ ...newI, category: e.target.value })} /></div>
                  <div><Label>{t("unit")}</Label><Input value={newI.unit} onChange={(e) => setNewI({ ...newI, unit: e.target.value })} /></div>
                  <div><Label>{t("quantity")}</Label><Input type="number" value={newI.quantity} onChange={(e) => setNewI({ ...newI, quantity: e.target.value })} /></div>
                  <div><Label>{t("threshold")}</Label><Input type="number" value={newI.min_threshold} onChange={(e) => setNewI({ ...newI, min_threshold: e.target.value })} /></div>
                  <div><Label>{t("unit_cost")}</Label><Input type="number" value={newI.unit_cost} onChange={(e) => setNewI({ ...newI, unit_cost: e.target.value })} /></div>
                  <div>
                    <Label>{t("supplier")}</Label>
                    <Select value={newI.supplier_id} onValueChange={(v) => setNewI({ ...newI, supplier_id: v })}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter><Button onClick={addItem} data-testid="item-save">{t("save")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="border-border">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left border-b border-border bg-secondary/50">
                  <tr>
                    <th className="p-3">{t("name")}</th><th>{t("category")}</th>
                    <th>{t("quantity")}</th><th>{t("threshold")}</th>
                    <th>{t("unit_cost")}</th><th>{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {stock.map((i) => {
                    const low = Number(i.quantity) <= Number(i.min_threshold);
                    return (
                      <tr key={i.id} className="border-b border-border/60 last:border-0">
                        <td className="p-3 font-medium flex items-center gap-2">
                          {low && <AlertTriangle className="w-4 h-4 text-[#CC5500]" />}{i.name}
                        </td>
                        <td>{i.category}</td>
                        <td>{i.quantity} {i.unit}</td>
                        <td>{i.min_threshold}</td>
                        <td>{formatXAF(i.unit_cost)}</td>
                        <td><Button variant="ghost" size="sm" onClick={() => delItem(i.id)} data-testid={`item-del-${i.id}`}><Trash2 className="w-4 h-4" /></Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suppliers" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={openS} onOpenChange={setOpenS}>
              <DialogTrigger asChild><Button data-testid="add-supplier-btn"><Plus className="w-4 h-4 mr-2" />{t("supplier")}</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("add")} — {t("supplier")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>{t("name")}</Label><Input data-testid="supplier-name" value={newS.name} onChange={(e) => setNewS({ ...newS, name: e.target.value })} /></div>
                  <div><Label>{t("category")}</Label><Input value={newS.category} onChange={(e) => setNewS({ ...newS, category: e.target.value })} /></div>
                  <div><Label>{t("city")}</Label><Input value={newS.city} onChange={(e) => setNewS({ ...newS, city: e.target.value })} /></div>
                  <div><Label>{t("phone")}</Label><Input value={newS.phone} onChange={(e) => setNewS({ ...newS, phone: e.target.value })} /></div>
                  <div><Label>{t("email")}</Label><Input value={newS.email} onChange={(e) => setNewS({ ...newS, email: e.target.value })} /></div>
                </div>
                <DialogFooter><Button onClick={addSupplier} data-testid="supplier-save">{t("save")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
            {suppliers.map((s) => (
              <Card key={s.id} className="card-hover border-border">
                <CardContent className="p-5">
                  <div className="label-eyebrow">{s.category || "—"}</div>
                  <div className="text-lg font-semibold mt-1">{s.name}</div>
                  <div className="text-sm text-muted-foreground mt-2 space-y-0.5">
                    <div>{s.city}</div>
                    <div>{s.phone}</div>
                    <div>{s.email}</div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => delSupplier(s.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="purchases" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={openPu} onOpenChange={setOpenPu}>
              <DialogTrigger asChild><Button data-testid="add-purchase-btn"><Plus className="w-4 h-4 mr-2" />{t("purchases")}</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("add")} — {t("purchases")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>{t("supplier")}</Label>
                    <Select value={newPu.supplier_id} onValueChange={(v) => setNewPu({ ...newPu, supplier_id: v })}>
                      <SelectTrigger data-testid="purchase-supplier"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>{t("product")}</Label><Input data-testid="purchase-item" value={newPu.item} onChange={(e) => setNewPu({ ...newPu, item: e.target.value })} /></div>
                  <div><Label>{t("category")}</Label><Input value={newPu.category} onChange={(e) => setNewPu({ ...newPu, category: e.target.value })} /></div>
                  <div><Label>{t("date")}</Label><Input type="date" value={newPu.date} onChange={(e) => setNewPu({ ...newPu, date: e.target.value })} /></div>
                  <div><Label>{t("quantity")}</Label><Input type="number" value={newPu.quantity} onChange={(e) => setNewPu({ ...newPu, quantity: e.target.value })} /></div>
                  <div><Label>{t("unit_cost")}</Label><Input type="number" value={newPu.unit_cost} onChange={(e) => setNewPu({ ...newPu, unit_cost: e.target.value })} /></div>
                </div>
                <DialogFooter><Button onClick={addPurchase} data-testid="purchase-save">{t("save")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger">
            {summary.map((s) => (
              <Card key={s.supplier_id} className="card-hover border-border" data-testid={`supplier-summary-${s.supplier_id}`}>
                <CardContent className="p-5">
                  <div className="label-eyebrow">{s.category || "—"}</div>
                  <div className="text-lg font-semibold mt-1">{s.name}</div>
                  <div className="mt-3 flex items-end justify-between">
                    <div>
                      <div className="text-xs text-muted-foreground">{t("total_spent")}</div>
                      <div className="text-xl font-bold text-primary">{formatXAF(s.total)}</div>
                    </div>
                    <Badge variant="secondary">{s.count} achats</Badge>
                  </div>
                  {Object.keys(s.by_category).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/60 space-y-1">
                      {Object.entries(s.by_category).map(([cat, amt]) => (
                        <div key={cat} className="flex justify-between text-xs text-muted-foreground">
                          <span>{cat}</span><span className="font-medium text-foreground">{formatXAF(amt)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-border">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left border-b border-border bg-secondary/50">
                  <tr>
                    <th className="p-3">{t("date")}</th><th>{t("supplier")}</th><th>{t("product")}</th>
                    <th>{t("category")}</th><th>{t("quantity")}</th><th>{t("total")}</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((p) => (
                    <tr key={p.id} className="border-b border-border/60 last:border-0">
                      <td className="p-3">{formatDate(p.date)}</td>
                      <td className="font-medium">{p.supplier_name}</td>
                      <td>{p.item}</td>
                      <td>{p.category}</td>
                      <td>{p.quantity}</td>
                      <td className="font-semibold">{formatXAF(p.total)}</td>
                      <td><Button variant="ghost" size="sm" onClick={() => delPurchase(p.id)}><Trash2 className="w-4 h-4" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
